import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { employeeApi } from '../lib/api';
import { formatDate, formatPrice, RESERVATION_STATUS } from '../lib/utils';
import { 
  Calendar, User, MapPin, Clock, Check, AlertTriangle, 
  FileText, Download, LogOut, ChevronRight, Phone
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import { EmployeePlanningCalendar } from '../components/EmployeePlanningCalendar';
import { SelectedOptionsSummary } from '../components/SelectedOptionsSummary';

export default function EmployeeDashboard() {
  const { user, logout } = useAuth();
  const { config } = useConfig();
  const navigate = useNavigate();
  const [interventions, setInterventions] = useState([]);
  const [planning, setPlanning] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('interventions');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [interventionsRes, planningRes] = await Promise.all([
        employeeApi.getInterventions(),
        employeeApi.getPlanning()
      ]);
      setInterventions(interventionsRes.data);
      setPlanning(planningRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (reservationId) => {
    try {
      await employeeApi.validateIntervention(reservationId);
      toast.success('Intervention validée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const upcomingInterventions = interventions.filter(i => ['confirmee', 'en_cours'].includes(i.status));
  const completedInterventions = interventions.filter(i => i.status === 'terminee');

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      {/* Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <Logo />
            </Link>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-[#5C6A61]">
                <User className="w-5 h-5" />
                <span className="hidden sm:inline">{user?.name}</span>
              </div>
              <button 
                onClick={handleLogout}
                className="text-[#5C6A61] hover:text-[#C86B51] transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="font-['Cormorant_Garamond'] text-3xl font-medium text-[#2A312C] mb-2">
            Espace Cuisinier
          </h1>
          <p className="text-[#5C6A61]">Gérez vos interventions et consultez votre planning</p>
        </div>

        {/* Quick stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="card-default">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#C86B51]/10 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[#C86B51]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#2A312C]">{upcomingInterventions.length}</p>
                <p className="text-sm text-[#5C6A61]">À venir</p>
              </div>
            </div>
          </div>
          <div className="card-default">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#A5BBA8]/20 flex items-center justify-center">
                <Check className="w-6 h-6 text-[#A5BBA8]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#2A312C]">{completedInterventions.length}</p>
                <p className="text-sm text-[#5C6A61]">Terminées</p>
              </div>
            </div>
          </div>
          <div className="card-default">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#E8B25C]/20 flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#E8B25C]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#2A312C]">{planning.filter(s => s.is_available).length}</p>
                <p className="text-sm text-[#5C6A61]">Créneaux dispo</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white border border-[#E3DED5]">
            <TabsTrigger value="interventions" className="data-[state=active]:bg-[#A5BBA8] data-[state=active]:text-white">
              Interventions
            </TabsTrigger>
            <TabsTrigger value="planning" className="data-[state=active]:bg-[#A5BBA8] data-[state=active]:text-white">
              Planning
            </TabsTrigger>
          </TabsList>

          <TabsContent value="interventions" data-testid="interventions-tab">
            {loading ? (
              <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>
            ) : upcomingInterventions.length === 0 ? (
              <div className="card-default text-center py-12">
                <Calendar className="w-12 h-12 text-[#E3DED5] mx-auto mb-4" />
                <h3 className="font-semibold text-[#2A312C] mb-2">Aucune intervention prévue</h3>
                <p className="text-[#5C6A61]">Vos prochaines interventions apparaîtront ici</p>
              </div>
            ) : (
              <div className="space-y-4">
                {upcomingInterventions.map((intervention) => (
                  <InterventionCard 
                    key={intervention.reservation_id}
                    intervention={intervention}
                    onValidate={handleValidate}
                  />
                ))}
              </div>
            )}

            {completedInterventions.length > 0 && (
              <div className="mt-8">
                <h2 className="font-semibold text-[#2A312C] mb-4">Historique</h2>
                <div className="space-y-4">
                  {completedInterventions.slice(0, 5).map((intervention) => (
                    <InterventionCard 
                      key={intervention.reservation_id}
                      intervention={intervention}
                      isPast
                    />
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="planning" data-testid="planning-tab">
            {loading ? (
              <div className="text-center py-12 text-[#5C6A61]">Chargement...</div>
            ) : (
              <EmployeePlanningCalendar
                interventions={interventions}
                planning={planning}
                onSelectIntervention={() => setActiveTab('interventions')}
              />
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function InterventionCard({ intervention, onValidate, isPast }) {
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [problemDialogOpen, setProblemDialogOpen] = useState(false);
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState('');
  
  const statusInfo = RESERVATION_STATUS[intervention.status];
  const client = intervention.client || {};

  const handleAddNote = async () => {
    try {
      await employeeApi.addNote(intervention.reservation_id, note);
      toast.success('Note ajoutée');
      setNoteDialogOpen(false);
      setNote('');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleReportProblem = async () => {
    try {
      await employeeApi.reportProblem(intervention.reservation_id, problem);
      toast.success('Problème signalé');
      setProblemDialogOpen(false);
      setProblem('');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <div className="card-default" data-testid={`intervention-card-${intervention.reservation_id}`}>
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Main info */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#2A312C]">
              {formatDate(intervention.date)}
            </h3>
            <Badge className={statusInfo?.color}>{statusInfo?.label}</Badge>
          </div>
          
          {/* Client info */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-[#5C6A61]">
              <User className="w-4 h-4" />
              <span>{client.name || 'Client'}</span>
            </div>
            {client.address && (
              <div className="flex items-center gap-2 text-[#5C6A61]">
                <MapPin className="w-4 h-4" />
                <span>{client.address}, {client.city}</span>
              </div>
            )}
            {client.phone && (
              <div className="flex items-center gap-2 text-[#5C6A61]">
                <Phone className="w-4 h-4" />
                <span>{client.phone}</span>
              </div>
            )}
            {intervention.start_time && (
              <div className="flex items-center gap-2 text-[#5C6A61]">
                <Clock className="w-4 h-4" />
                <span>{intervention.start_time}{intervention.end_time ? ` – ${intervention.end_time}` : ''}</span>
              </div>
            )}
          </div>
          
          {/* Details */}
          <div className="mt-3 p-3 bg-[#F9F6F0] rounded-xl">
            <p className="text-sm text-[#2A312C]">
              <strong>{intervention.household_size}</strong> personne(s) • Formule <strong>{intervention.formula}</strong>
            </p>
            {intervention.options && Object.entries(intervention.options).some(([k, v]) => v && k !== 'drive') && (
              <p className="text-xs text-[#5C6A61] mt-1" data-testid={`intervention-options-${intervention.reservation_id}`}>
                Options : {Object.entries(intervention.options).filter(([k, v]) => v && k !== 'drive').map(([k]) => ({ lunch: 'Lunch', dessert: 'Dessert', patisserie: 'Pâtisserie' }[k] || k)).join(', ')}
              </p>
            )}
            <SelectedOptionsSummary selectedOptions={intervention.selected_options} className="mt-2" />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 lg:items-end">
          {!isPast && intervention.status === 'confirmee' && (
            <button
              onClick={() => onValidate(intervention.reservation_id)}
              className="btn-primary text-sm px-4 py-2"
              data-testid={`validate-btn-${intervention.reservation_id}`}
            >
              <Check className="w-4 h-4 mr-2" />
              Valider
            </button>
          )}
          
          <a
            href={employeeApi.downloadFichePdf(intervention.reservation_id)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline text-sm px-4 py-2 inline-flex items-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Fiche
          </a>

          <div className="flex gap-2">
            <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
              <DialogTrigger asChild>
                <button className="btn-outline text-sm px-3 py-2">
                  <FileText className="w-4 h-4" />
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ajouter une note</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="input-default min-h-[100px]"
                    placeholder="Votre note..."
                  />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setNoteDialogOpen(false)} className="btn-outline">Annuler</button>
                    <button onClick={handleAddNote} className="btn-primary">Enregistrer</button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={problemDialogOpen} onOpenChange={setProblemDialogOpen}>
              <DialogTrigger asChild>
                <button className="text-[#C86B51] hover:bg-[#C86B51]/10 p-2 rounded-lg transition-colors">
                  <AlertTriangle className="w-4 h-4" />
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Signaler un problème</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <textarea
                    value={problem}
                    onChange={(e) => setProblem(e.target.value)}
                    className="input-default min-h-[100px]"
                    placeholder="Décrivez le problème..."
                  />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setProblemDialogOpen(false)} className="btn-outline">Annuler</button>
                    <button onClick={handleReportProblem} className="btn-secondary">Signaler</button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}
